// src/views/AboutPage.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Shield, Zap, Target, ChevronDown } from 'lucide-react';
import founderPhoto from '../assets/about-founder.jpg';
import founderHero from '../assets/founder-hero.jpg';


const AboutPage = () => {
  const APP_SIGNUP_URL = "https://app.spatchy.co/pricing";

  return (
    <div className="bg-slate-900 min-h-screen text-white pt-24 pb-32 font-sans selection:bg-blue-500 selection:text-white">
      <Helmet>
        <title>About | Spatchy</title>
        <meta name="description" content="Spatchy was built by a business owner who ran 9 companies and got tired of being nickle-and-dimed by software that should have been simpler. Here's the real story." />
      </Helmet>

      {/* --- HERO --- */}
      <div className="text-center max-w-4xl mx-auto px-6 mb-24 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase tracking-wide mb-6">
          The Story Behind Spatchy
        </div>
        <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight leading-tight">
          Built by a business owner. <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
            For business owners.
          </span>
        </h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12">
          Not by a VC-backed team in a WeWork. By someone who has owned nine businesses, used every tool on the market, and got fed up enough to build something better.
        </p>
        <div className="flex justify-center opacity-50 animate-bounce">
          <ChevronDown className="text-white" size={32} />
        </div>
      </div>

      {/* --- ORIGIN STORY --- */}
      <section className="px-6 mb-32">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">

          {/* Image Side */}
          <div className="relative group">
            <div className="absolute inset-0 bg-blue-500/20 rounded-2xl blur-2xl group-hover:bg-blue-500/30 transition-all duration-500"></div>
            <div className="relative bg-slate-800 rounded-2xl border border-white/10 aspect-[4/3] shadow-2xl overflow-hidden">
             <img src={founderHero} alt="Marcus Luft" className="w-full h-full object-cover object-top" />
            </div>
          </div>

          {/* Text Side */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Nine businesses. One breaking point.</h2>
            <div className="space-y-5 text-slate-400 text-lg leading-relaxed">
              <p>
                Over the course of my career I've owned and operated nine businesses across different industries. With each one came the same ritual — find the software, pay the subscription, deal with the frustration.
              </p>
              <p>
                The frustration finally came to a head when I was running a residential cleaning company and started using Jobber. On the surface it seemed like the right tool. But the longer I used it, the more I felt like I was being punished for wanting functionality that should have just been included. Features locked behind higher tiers. Constant upsells. A pricing model designed to extract more as your team grows.
              </p>
              <p>
                I looked at every alternative on the market. They all had the same problem — feature gating, fragmented tools, and a fundamental disconnect between what the software cost and what it actually delivered. You'd end up juggling five subscriptions just to run a single business.
              </p>
              <p>
                I'd had the idea for Spatchy in the back of my mind for years. What stopped me was that I'm not a developer. What changed everything was the rise of agentic AI — it finally opened the door for someone like me to actually build the product I'd been dreaming about. So I did.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* --- WHAT MAKES SPATCHY DIFFERENT --- */}
      <section className="px-6 mb-32">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">The promise we built Spatchy on</h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Three principles that every product decision gets measured against.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-800/50 p-8 rounded-2xl border border-white/5 hover:border-blue-500/30 transition-colors">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-400 mb-6">
                <Shield size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">No Feature Gating. Ever.</h3>
              <p className="text-slate-400 leading-relaxed">
                Every feature Spatchy has is available on every plan, from day one. You pay for team size — that's it. No upsells, no "upgrade to unlock," no surprises.
              </p>
            </div>

            <div className="bg-slate-800/50 p-8 rounded-2xl border border-white/5 hover:border-purple-500/30 transition-colors">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center text-purple-400 mb-6">
                <Zap size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">One App. One Login.</h3>
              <p className="text-slate-400 leading-relaxed">
                CRM, scheduling, invoicing, HR, email marketing, reporting — all of it inside Spatchy. No more stitching together five subscriptions and logging in and out all day.
              </p>
            </div>

            <div className="bg-slate-800/50 p-8 rounded-2xl border border-white/5 hover:border-emerald-500/30 transition-colors">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center text-emerald-400 mb-6">
                <Target size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Value That Speaks for Itself.</h3>
              <p className="text-slate-400 leading-relaxed">
                The goal was to build something so packed with utility that the price feels like a no-brainer. And to keep it that way — no hidden price hikes, no eroding the deal over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- FOUNDER NOTE --- */}
      <section className="px-6 mb-32">
        <div className="max-w-3xl mx-auto bg-slate-800/50 border border-white/10 rounded-3xl p-8 md:p-12 relative overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] -z-0"></div>

          <div className="relative flex flex-col md:flex-row gap-8 items-center md:items-start">

            {/* Founder Photo */}
            <div className="w-28 h-28 flex-shrink-0 bg-slate-700 rounded-full overflow-hidden border-4 border-slate-600 shadow-xl">
                <img src={founderPhoto} alt="Marcus Luft" className="w-full h-full object-cover" />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-white mb-1">Marcus Luft</h3>
              <p className="text-blue-400 font-medium mb-6">Founder, Spatchy · Thunder Bay, Ontario</p>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p className="italic text-lg">
                  "I didn't build Spatchy to compete with Jobber. I built it because I was a customer of tools like Jobber and kept feeling like I was getting the short end of the deal. The features I needed were always one tier up. The pricing always crept higher. I was always being asked to pay more for less."
                </p>
                <p className="italic">
                  "My mission is straightforward: give small business owners a complete, powerful system at a price that makes sense, and never change the deal on them. The moment Spatchy starts gate-keeping features or hiking prices, we've become the thing I built this to replace. That won't happen."
                </p>
                <p className="text-slate-400 text-sm mt-4 not-italic">
                  — Marcus has owned and operated nine businesses spanning multiple industries and built Spatchy without a technical background using modern AI development tools.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- BOTTOM CTA --- */}
      <section className="text-center px-6">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-900/40 to-slate-900 border border-blue-500/20 rounded-3xl p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -z-10"></div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to make the switch?</h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            {/* wave1-fix c1a4f627: real trial is 30 days, not 14 */}
            No credit card required. No features locked away. Just a 30-day free trial of everything Spatchy has to offer.
          </p>
          <a
            href={APP_SIGNUP_URL}
            className="inline-flex px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-full transition-all shadow-lg shadow-blue-900/50 items-center gap-2"
          >
            Start Your Free Trial <ArrowRight size={18} />
          </a>
          <p className="text-slate-600 text-sm mt-4">
            Cancel anytime. No questions asked.
          </p>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;